import type { View } from '../app/views/types';
import { useState, useEffect } from 'react';
import { Storage } from '../lib/storage';

// ─────────────────────────────────────────────────────────────────────
// useDrafts — the saved-setup ("drafts") list shown on the Home hub. Owns the
// `drafts` state + its refresh-on-home effect + the 3 handlers (refresh/delete/
// open). Extracted verbatim from `Padel24AppMain` in the former monolith
// (legacy/App.jsx) as the first slice of the AppShell/god-component collapse
// (PR-AQ). No persisted state of
// its own beyond reading the per-format `*_setups_v1` Storage keys; the nav
// setters it needs are passed in via `deps`.
// ─────────────────────────────────────────────────────────────────────
export interface UseDraftsDeps {
  view: string;
  setView: (v: View) => void;
  setPendingSetupLoad: (v: any) => void;
  setSetupFromActivities: (v: any) => void;
  setCustomActivityType: (v: any) => void;
}

export function useDrafts({
  view,
  setView,
  setPendingSetupLoad,
  setSetupFromActivities,
  setCustomActivityType,
}: UseDraftsDeps) {
  const [drafts, setDrafts] = useState<any[]>([]);

  const refreshDrafts = async () => {
    const sources = [
      { key: 'padel24_setups_v1', type: 'padel24', typeLabel: 'Padel24' },
      { key: 'americano_setups_v1', type: 'americano', typeLabel: 'Americano' },
      { key: 'team_americano_setups_v1', type: 'team_americano', typeLabel: 'Team Americano' },
      { key: 'mexicano_setups_v1', type: 'mexicano', typeLabel: 'Mexicano' },
      { key: 'mixicano_setups_v1', type: 'mixicano', typeLabel: 'Mix Mexicano' },
      { key: 'team_mexicano_setups_v1', type: 'team_mexicano', typeLabel: 'Team Mexicano' },
      { key: 'mix_americano_setups_v1', type: 'mix_americano', typeLabel: 'Mix Americano' },
      { key: 'club_americano_setups_v1', type: 'club_americano', typeLabel: 'Club Americano' },
      {
        key: 'club_team_americano_setups_v1',
        type: 'club_team_americano',
        typeLabel: 'Club Team Americano',
      },
      { key: 'club_mexicano_setups_v1', type: 'club_mexicano', typeLabel: 'Club Mexicano' },
      { key: 'klubbmesterskap_setups_v1', type: 'klubbmesterskap', typeLabel: 'Klubbmesterskap' },
    ];
    // ONE LISTING, THEN ONLY WHAT EXISTS (#1145, AUD-031). Reading all eleven
    // keys answered a 404 for every format the club never saved a setup for —
    // eleven red lines in every portal console, burying real errors and riding
    // along in every bug report. The club's key list is one request and says
    // which are there. A failed listing reads as «none», which is what eleven
    // failed reads gave as well.
    let stored = new Set<string>();
    try {
      stored = new Set((await Storage.list('')).keys ?? []);
    } catch {
      // As above: no listing reads as «none».
    }
    const all = [];
    for (const src of sources) {
      if (!stored.has(src.key)) continue;
      try {
        const res = await Storage.get(src.key);
        if (res?.value) {
          const list = JSON.parse(res.value);
          if (Array.isArray(list)) {
            for (const s of list) {
              // Each setup has: id, name, config, selectedPlayerIds, customPlayers,
              // playerOverrides, and optionally proposedGroups, lockedPlayers.
              const playerCount = Array.isArray(s.selectedPlayerIds)
                ? s.selectedPlayerIds.length
                : 0;
              all.push({
                key: src.key,
                setupId: s.id,
                type: src.type,
                typeLabel: src.typeLabel,
                name: s.name || src.typeLabel,
                playerCount,
                hasGroups: !!(s.proposedGroups && s.proposedGroups.length > 0),
                savedAt: s.updatedAt || s.createdAt || 0,
              });
            }
          }
        }
      } catch {
        // Ignore corrupt data
      }
    }
    // Sort by last saved (newest first)
    all.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
    setDrafts(all);
  };

  const deleteDraft = async (draft) => {
    try {
      const res = await Storage.get(draft.key);
      if (res?.value) {
        const list = JSON.parse(res.value);
        if (Array.isArray(list)) {
          const updated = list.filter((s) => s.id !== draft.setupId);
          await Storage.set(draft.key, JSON.stringify(updated));
        }
      }
      await refreshDrafts();
    } catch (e) {
      console.warn('[Setups] Kunne ikke slette oppsett:', e);
    }
  };

  const openDraft = (draft) => {
    setSetupFromActivities(false);
    setCustomActivityType(null);
    setPendingSetupLoad({ key: draft.key, setupId: draft.setupId });
    if (draft.type === 'padel24') setView('wizard');
    else if (draft.type === 'americano') setView('americanoSetup');
    else if (draft.type === 'team_americano') setView('teamAmericanoSetup');
    else if (draft.type === 'mexicano') setView('mexicanoSetup');
    else if (draft.type === 'mixicano') setView('mixicanoSetup');
    else if (draft.type === 'team_mexicano') setView('teamMexicanoSetup');
    else if (draft.type === 'mix_americano') setView('mixAmericanoSetup');
    else if (draft.type === 'club_americano') setView('clubAmericanoSetup');
    else if (draft.type === 'club_team_americano') setView('clubTeamAmericanoSetup');
    else if (draft.type === 'club_mexicano') setView('clubMexicanoSetup');
  };

  useEffect(() => {
    if (view === 'home') {
      refreshDrafts();
    }
  }, [view]);

  return { drafts, refreshDrafts, deleteDraft, openDraft };
}
